import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';

import { useCreateClient } from '@/lib/clients-query';
import type { CreateClientPayload } from '@/lib/clients.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

/**
 * Wizard de intake en 3 pasos para crear un cliente:
 *  1. Identidad (fullName, SSN last4, DOB).
 *  2. Dirección completa + contacto.
 *  3. Confirmación + autorización (checkbox "I authorize…").
 *
 * Validación: un único schema Zod cubre los 3 pasos. El wizard valida por
 * paso justo antes de avanzar (con un subconjunto de campos).
 */

const intakeSchema = z.object({
  fullName: z.string().min(2, 'Enter the client full name.').max(160),
  ssnLast4: z
    .string()
    .length(4, 'SSN last-4 must be exactly 4 characters.')
    .regex(/^[0-9]{4}$/, 'SSN last-4 must be 4 digits.'),
  dob: z
    .string()
    .min(10, 'Date of birth is required.')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use ISO format YYYY-MM-DD.'),
  address: z.string().min(4, 'Street address is required.').max(240),
  city: z.string().min(2, 'City is required.').max(120),
  state: z
    .string()
    .length(2, 'State must be exactly 2 characters.')
    .regex(/^[A-Z]{2}$/, 'Use uppercase 2-letter state code (e.g. TX).'),
  zip: z
    .string()
    .min(5, 'ZIP is required.')
    .max(16)
    .regex(/^[0-9A-Za-z][0-9A-Za-z- ]{3,15}$/, 'Invalid ZIP/Postal code.'),
  phone: z.string().max(40).optional(),
  email: z.string().email('Invalid email.').optional().or(z.literal('')),
  intakeCompleted: z.boolean(),
});

type IntakeValues = z.infer<typeof intakeSchema>;

const STEPS = ['Identity', 'Address & contact', 'Review & authorize'] as const;

const AUTH_TEXT =
  'I authorize RepairPulse and its contracted agency to act as my agent in disputing inaccurate, incomplete, or unverifiable information on my consumer credit reports, and to send dispute correspondence on my behalf to the credit bureaus.';

export function ClientIntake() {
  const navigate = useNavigate();
  const create = useCreateClient();
  const [step, setStep] = useState(0);
  const [authorized, setAuthorized] = useState(false);

  const form = useForm<IntakeValues>({
    resolver: zodResolver(intakeSchema),
    defaultValues: {
      fullName: '',
      ssnLast4: '',
      dob: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      intakeCompleted: false,
    },
    mode: 'onTouched',
  });

  const next = async (): Promise<void> => {
    // Validar cada paso con un subconjunto de campos
    const fieldsToValidate: (keyof IntakeValues)[] =
      step === 0
        ? ['fullName', 'ssnLast4', 'dob']
        : step === 1
          ? ['address', 'city', 'state', 'zip', 'phone', 'email']
          : [];
    const ok = await form.trigger(fieldsToValidate);
    if (!ok) return;
    if (step === 2 && !authorized) return; // require checkbox
    setStep((s) => Math.min(2, s + 1));
  };
  const prev = (): void => setStep((s) => Math.max(0, s - 1));

  const submit = form.handleSubmit(async (values) => {
    if (!authorized) return;
    const payload: CreateClientPayload = {
      fullName: values.fullName,
      ssnLast4: values.ssnLast4,
      dob: values.dob,
      address: values.address,
      city: values.city,
      state: values.state,
      zip: values.zip,
      phone: values.phone?.trim() || undefined,
      email: values.email?.trim() || undefined,
      intakeCompleted: true,
    };
    const created = await create.mutateAsync(payload);
    navigate(`/clients/${created.id}`, { replace: true });
  });

  return (
    <section className="mx-auto max-w-2xl px-6 py-10 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight">
            New client intake
          </h1>
          <p className="text-sm text-muted-foreground">Step {step + 1} of 3 · {STEPS[step]}</p>
        </div>

        {/* Stepper visual */}
        <ol className="flex items-center gap-2 text-xs">
          {STEPS.map((label, i) => (
            <li key={label} className="flex items-center gap-2">
              <span
                className={`size-7 rounded-full flex items-center justify-center border ${
                  i < step
                    ? 'bg-primary text-primary-foreground border-primary'
                    : i === step
                      ? 'border-primary text-primary'
                      : 'border-border text-muted-foreground'
                }`}
              >
                {i < step ? <Check className="size-3.5" /> : i + 1}
              </span>
              <span className={i === step ? 'font-medium' : 'text-muted-foreground'}>
                {label}
              </span>
              {i < STEPS.length - 1 && <span className="text-muted-foreground">→</span>}
            </li>
          ))}
        </ol>

        <Card>
          <form onSubmit={submit}>
            {/* STEP 1 — Identity */}
            {step === 0 && (
              <>
                <CardHeader>
                  <CardTitle>Identity</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full name</Label>
                    <Input id="fullName" placeholder="Jane Doe" {...form.register('fullName')} />
                    {form.formState.errors.fullName && (
                      <p className="text-sm text-destructive">{form.formState.errors.fullName.message}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ssnLast4">SSN last 4</Label>
                      <Input
                        id="ssnLast4"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="•••4"
                        autoComplete="off"
                        {...form.register('ssnLast4')}
                      />
                      {form.formState.errors.ssnLast4 && (
                        <p className="text-sm text-destructive">{form.formState.errors.ssnLast4.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dob">Date of birth</Label>
                      <Input id="dob" type="date" {...form.register('dob')} />
                      {form.formState.errors.dob && (
                        <p className="text-sm text-destructive">{form.formState.errors.dob.message}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Only the last 4 digits of the SSN are stored. Never share the full number.
                  </p>
                </CardContent>
              </>
            )}

            {/* STEP 2 — Address */}
            {step === 1 && (
              <>
                <CardHeader>
                  <CardTitle>Address & contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="address">Street address</Label>
                    <Input id="address" placeholder="789 Pine St" {...form.register('address')} />
                    {form.formState.errors.address && (
                      <p className="text-sm text-destructive">{form.formState.errors.address.message}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="city">City</Label>
                      <Input id="city" {...form.register('city')} />
                      {form.formState.errors.city && (
                        <p className="text-sm text-destructive">{form.formState.errors.city.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input id="state" maxLength={2} placeholder="TX" {...form.register('state')} />
                      {form.formState.errors.state && (
                        <p className="text-sm text-destructive">{form.formState.errors.state.message}</p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip">ZIP / Postal code</Label>
                    <Input id="zip" placeholder="78701" {...form.register('zip')} />
                    {form.formState.errors.zip && (
                      <p className="text-sm text-destructive">{form.formState.errors.zip.message}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" placeholder="555-1234" {...form.register('phone')} />
                      {form.formState.errors.phone && (
                        <p className="text-sm text-destructive">{form.formState.errors.phone.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" placeholder="jane@example.com" {...form.register('email')} />
                      {form.formState.errors.email && (
                        <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </>
            )}

            {/* STEP 3 — Review */}
            {step === 2 && (
              <>
                <CardHeader>
                  <CardTitle>Review & authorize</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
                    <ReviewRow label="Full name" value={form.getValues('fullName')} />
                    <ReviewRow label="SSN last-4" value={`•••${(form.getValues('ssnLast4') || '•').slice(-1)}`} />
                    <ReviewRow label="DOB" value={form.getValues('dob')} />
                    <ReviewRow label="Address" value={form.getValues('address')} />
                    <ReviewRow label="City, State ZIP"
                      value={`${form.getValues('city')}, ${form.getValues('state')} ${form.getValues('zip')}`} />
                    <ReviewRow label="Phone" value={form.getValues('phone') || '—'} />
                    <ReviewRow label="Email" value={form.getValues('email') || '—'} />
                  </dl>

                  <label className="flex items-start gap-3 cursor-pointer rounded-md border p-4 hover:bg-muted/40">
                    <input
                      type="checkbox"
                      checked={authorized}
                      onChange={(e) => setAuthorized(e.target.checked)}
                      className="mt-1 size-4 accent-primary"
                    />
                    <span className="text-sm text-muted-foreground">{AUTH_TEXT}</span>
                  </label>
                  {!authorized && (
                    <p className="text-sm text-muted-foreground">
                      You must check the authorization box to continue.
                    </p>
                  )}
                </CardContent>
              </>
            )}

            {create.isError && (
              <p className="px-6 pb-4 text-sm text-destructive">
                Failed to create client. The agency may have hit its plan limit.
              </p>
            )}

            <CardFooter className="flex justify-between">
              <Button type="button" variant="outline" onClick={prev} disabled={step === 0}>
                <ArrowLeft className="size-4 mr-1" /> Back
              </Button>
              {step < 2 ? (
                <Button type="button" onClick={next}>
                  Next <ArrowRight className="size-4 ml-1" />
                </Button>
              ) : (
                <Button type="submit" disabled={!authorized || create.isPending}>
                  {create.isPending && <Loader2 className="size-4 mr-1 animate-spin" />}
                  {create.isPending ? 'Creating…' : 'Create client'}
                </Button>
              )}
            </CardFooter>
          </form>
        </Card>
    </section>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs text-muted-foreground uppercase tracking-wide">{label}</dt>
      <dd className="font-medium">{value || '—'}</dd>
    </div>
  );
}